import { Controller } from "@hotwired/stimulus"

// The lair's side column as tabs. Every panel stays in the page, hidden or
// not, because Turbo streams replace panels by id and links from elsewhere
// land on them by anchor (#training, #quests-panel, #verify-address…). So
// an anchor decides the tab: the one holding it opens, and a folded
// <details> around it unfolds. Otherwise the default opens: Dragon, whose
// forms are the care verbs on a phone.

export default class extends Controller {
  static targets = [ "tab", "panel" ]
  static values = { default: String }

  connect() {
    this.onHash = () => this.reveal(location.hash)
    this.onClick = this.onClick.bind(this)
    this.onStream = this.onStream.bind(this)
    window.addEventListener("hashchange", this.onHash)
    document.addEventListener("click", this.onClick)
    document.addEventListener("turbo:before-stream-render", this.onStream)

    if (!this.reveal(location.hash)) this.show(this.defaultValue || this.names[0])
  }

  disconnect() {
    window.removeEventListener("hashchange", this.onHash)
    document.removeEventListener("click", this.onClick)
    document.removeEventListener("turbo:before-stream-render", this.onStream)
  }

  // The panels are replaced whole — after every act of care, and on the
  // gauges' timer — and the new markup arrives folded. What the keeper had
  // unfolded stays unfolded.
  onStream(event) {
    const render = event.detail.render
    event.detail.render = async (stream) => {
      const open = [ ...this.element.querySelectorAll("details[open][id]") ].map((fold) => fold.id)
      await render(stream)
      open.forEach((id) => {
        const fold = document.getElementById(id)
        if (fold && this.element.contains(fold)) fold.open = true
      })
    }
  }

  get names() {
    return this.tabTargets.map((tab) => tab.dataset.tab)
  }

  select(event) {
    this.show(event.currentTarget.dataset.tab)
  }

  // Left and right walk the tabs, Home and End jump, as a tablist should.
  step(event) {
    const names = this.names
    const at = names.indexOf(event.currentTarget.dataset.tab)
    const next = {
      ArrowRight: names[(at + 1) % names.length],
      ArrowLeft: names[(at - 1 + names.length) % names.length],
      Home: names[0],
      End: names[names.length - 1]
    }[event.key]
    if (!next) return

    event.preventDefault()
    this.show(next)
    this.tabTargets.find((tab) => tab.dataset.tab === next)?.focus()
  }

  show(name) {
    if (!this.names.includes(name)) name = this.names[0]
    this.tabTargets.forEach((tab) => {
      const on = tab.dataset.tab === name
      tab.setAttribute("aria-selected", String(on))
      tab.tabIndex = on ? 0 : -1
    })
    this.panelTargets.forEach((panel) => { panel.hidden = panel.dataset.tab !== name })
  }

  // Opens whatever holds the anchor; true when it was in one of the tabs.
  reveal(hash) {
    if (!hash || hash.length < 2) return false

    let target
    try {
      target = this.element.querySelector(decodeURIComponent(hash))
    } catch {
      return false
    }
    const panel = target?.closest("[data-rail-tabs-target~='panel']")
    if (!panel) return false

    this.show(panel.dataset.tab)
    for (let fold = target.closest("details"); fold; fold = fold.parentElement?.closest("details")) {
      fold.open = true
    }
    requestAnimationFrame(() => target.scrollIntoView({ block: "start" }))
    return true
  }

  // A same-page link to a panel in a hidden tab does not fire hashchange when
  // the hash is already the one it names, so it is opened from the click.
  onClick(event) {
    const link = event.target.closest?.("a[href*='#']")
    if (!link || link.origin !== location.origin || link.pathname !== location.pathname) return
    if (link.hash) this.reveal(link.hash)
  }
}
